<template>
  <div class="question-mastery-trend">
    <div class="header">
      <div class="title">题目掌握趋势</div>
      <div class="operations">
        <el-tooltip content="导出图片" placement="top">
          <el-button type="text" icon="el-icon-download" @click="handleExport" />
        </el-tooltip>
        <el-tooltip content="放大显示" placement="top">
          <el-button type="text" icon="el-icon-full-screen" @click="handleFullscreen" />
        </el-tooltip>
      </div>
    </div>
    <div class="chart-container" ref="chart">
      <div class="chart-content">
        <div class="y-axis-label">题目数量</div>
      </div>
    </div>

    <!-- 全屏弹窗 -->
    <el-dialog
      :visible.sync="dialogVisible"
      :append-to-body="true"
      :modal-append-to-body="false"
      :close-on-click-modal="false"
      width="90%"
      :destroy-on-close="false"
      :show-close="false"
      class="chart-dialog vertical-center-dialog"
      @closed="handleDialogClosed"
      @open="handleDialogOpen"
      @close="handleDialogClose"
    >
      <div slot="title" class="dialog-header">
        <span>题目掌握趋势</span>
        <div class="dialog-operations">
          <el-tooltip content="导出图片" placement="top">
            <el-button type="text" icon="el-icon-download" @click="handleExportFullscreen" />
          </el-tooltip>
          <el-tooltip content="关闭" placement="top">
            <el-button type="text" icon="el-icon-close" @click="closeDialog" />
          </el-tooltip>
        </div>
      </div>
      <div class="fullscreen-chart" ref="fullscreenChart"></div>
    </el-dialog>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import request from '@/utils/request'
import _ from 'lodash'

export default {
  name: 'QuestionMasteryTrend',
  data() {
    return {
      chart: null,
      chartData: {
        months: [],
        totalQuestions: [],
        acQuestions: [],
        fixedQuestions: [],
        unfixedQuestions: [],
        masteryRates: []
      },
      fullscreenChart: null,
      dialogVisible: false,
      resizeHandler: null,
      isInitializing: false
    }
  },
  mounted() {
    this.initChart()
    this.fetchData()
    window.addEventListener('resize', this.resizeChart)
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.dispose()
    }
    if (this.fullscreenChart) {
      this.fullscreenChart.dispose()
    }
    window.removeEventListener('resize', this.resizeChart)
  },
  methods: {
    initChart() {
      this.chart = echarts.init(this.$refs.chart)
    },
    resizeChart() {
      this.chart?.resize()
    },
    async fetchData() {
      try {
        const res = await request.get('/api/analysis/question/mastery_trend')
        this.chartData = {
          months: res.data.months || [],
          totalQuestions: res.data.total_questions || [],
          acQuestions: res.data.ac_questions || [],
          fixedQuestions: res.data.fixed_questions || [],
          unfixedQuestions: res.data.unfixed_questions || [],
          masteryRates: res.data.mastery_rates || []
        }
        this.updateChart()
      } catch (error) {
        console.error('获取数据失败:', error)
        this.$message.error('获取数据失败')
      }
    },
    updateChart() {
      const option = {
        backgroundColor: '#ffffff',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'shadow'
          },
          backgroundColor: 'rgba(50, 50, 50, 0.9)',
          borderColor: '#333',
          textStyle: {
            color: '#fff',
            fontSize: 12
          },
          formatter: (params) => {
            if (!params || !params.length) return ''
            const title = params[0].axisValue || ''
            let content = ''
            params.forEach(param => {
              if (param.seriesName === '掌握率') {
                content += `<div>${param.seriesName}：${param.value}%</div>`
              } else {
                content += `<div>${param.seriesName}：${param.value}</div>`
              }
            })
            return `
              <div style="padding: 4px;">
                <div style="font-weight: bold; margin-bottom: 6px;">${title}</div>
                <div style="margin-top: 6px; padding-top: 6px; border-top: 1px solid rgba(255, 255, 255, 0.3);">
                  ${content}
                </div>
              </div>
            `
          }
        },
        legend: {
          data: ['题目总数', 'AC数量', '已订正数量', '未订正数量', '掌握率'],
          bottom: 0,
          left: 'center',
          padding: [0, 0, 0, 0]
        },
        grid: {
          left: '3%',
          right: '4%',
          bottom: '10%',
          top: '3%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: this.chartData.months,
          axisTick: {
            alignWithLabel: true
          }
        },
        yAxis: [
          {
            type: 'value',
            name: '题目数量',
            minInterval: 1,
            splitLine: {
              show: true,
              lineStyle: {
                type: 'dashed'
              }
            }
          },
          {
            type: 'value',
            name: '掌握率',
            min: 0,
            max: 100,
            interval: 20,
            axisLabel: {
              formatter: '{value}%'
            },
            splitLine: {
              show: false
            }
          }
        ],
        series: [
          {
            name: '题目总数',
            type: 'bar',
            barMaxWidth: 20,
            itemStyle: {
              color: '#409EFF',
              borderRadius: [4, 4, 0, 0]
            },
            emphasis: {
              focus: 'series'
            },
            data: this.chartData.totalQuestions
          },
          {
            name: 'AC数量',
            type: 'bar',
            stack: 'status',
            barMaxWidth: 20,
            itemStyle: {
              color: '#67C23A',
              borderRadius: [4, 4, 0, 0]
            },
            emphasis: {
              focus: 'series'
            },
            data: this.chartData.acQuestions
          },
          {
            name: '已订正数量',
            type: 'bar',
            stack: 'status',
            barMaxWidth: 20,
            itemStyle: {
              color: '#95DE64',
              borderRadius: [4, 4, 0, 0]
            },
            emphasis: {
              focus: 'series'
            },
            data: this.chartData.fixedQuestions
          },
          {
            name: '未订正数量',
            type: 'bar',
            stack: 'status',
            barMaxWidth: 20,
            itemStyle: {
              color: 'rgba(144, 147, 153, 0.7)',
              borderRadius: [4, 4, 0, 0]
            },
            emphasis: {
              focus: 'series'
            },
            data: this.chartData.unfixedQuestions
          },
          {
            name: '掌握率',
            type: 'line',
            yAxisIndex: 1,
            smooth: true,
            showSymbol: true,
            symbolSize: 8,
            lineStyle: {
              width: 3,
              color: '#E6A23C'
            },
            itemStyle: {
              color: '#E6A23C'
            },
            emphasis: {
              focus: 'series'
            },
            data: this.chartData.masteryRates
          }
        ]
      }

      this.chart.setOption(option, true)
    },
    handleExport() {
      const dataURL = this.chart.getDataURL({
        type: 'png',
        pixelRatio: 2,
        backgroundColor: '#fff'
      })
      
      const link = document.createElement('a')
      link.download = '题目掌握趋势.png'
      link.href = dataURL
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    handleDialogOpen() {
      if (this.isInitializing) return
      this.isInitializing = true

      this.$nextTick(() => {
        try {
          const chartDom = this.$refs.fullscreenChart
          if (!chartDom) return

          this.fullscreenChart = echarts.init(chartDom)
          const option = this.chart.getOption()
          
          option.grid = {
            left: '5%',
            right: '5%',
            bottom: '10%',
            top: '10%',
            containLabel: true
          }

          this.fullscreenChart.setOption(option)
          this.resizeHandler = _.debounce(() => {
            if (this.fullscreenChart) {
              this.fullscreenChart.resize()
            }
          }, 100)
          window.addEventListener('resize', this.resizeHandler)
        } catch (error) {
          console.error('初始化全屏图表失败:', error)
          this.$message.error('初始化图表失败')
        } finally {
          this.isInitializing = false
        }
      })
    },
    handleDialogClose() {
      this.cleanupFullscreen()
    },
    handleDialogClosed() {
      this.isInitializing = false
    },
    cleanupFullscreen() {
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
        this.resizeHandler = null
      }

      if (this.fullscreenChart) {
        this.fullscreenChart.dispose()
        this.fullscreenChart = null
      }
    },
    handleFullscreen() {
      this.cleanupFullscreen()
      this.dialogVisible = true
    },
    handleExportFullscreen() {
      if (!this.fullscreenChart) return
      
      try {
        const dataURL = this.fullscreenChart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff'
        })
        
        const link = document.createElement('a')
        link.download = '题目掌握趋势.png'
        link.href = dataURL
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } catch (error) {
        console.error('导出图片失败:', error)
        this.$message.error('导出图片失败')
      }
    },
    closeDialog() {
      this.dialogVisible = false
    }
  }
}
</script>

<style scoped>
.question-mastery-trend {
  width: 100%;
  height: 100%;
  padding: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-radius: 8px 8px 0 0;
}

.title {
  font-weight: normal;
  color: #ffffff;
}

.operations {
  display: flex;
  gap: 8px;
  align-items: center;
}

.operations .el-button {
  color: #ffffff;
}

.operations .el-button:hover {
  color: #BD6C73;
}

.chart-container {
  height: 400px;
  width: 100%;
  padding: 20px;
  box-sizing: border-box;
}

.chart-dialog :deep(.el-dialog__header) {
  padding: 0;
  margin: 0;
}

.chart-dialog :deep(.el-dialog__headerbtn) {
  display: none;
}

.chart-dialog :deep(.el-dialog__body) {
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 17px 20px;
  height: 16px;
  box-sizing: content-box;
  background-color: #2E365A;
  color: #ffffff;
  border-bottom: none;
}

.dialog-operations {
  display: flex;
  gap: 8px;
}

.dialog-operations .el-button {
  color: #ffffff;
}

.dialog-operations .el-button:hover {
  color: #BD6C73;
}

.fullscreen-chart {
  width: 100%;
  height: 600px;
  padding: 20px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style> 